import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatedRecommendation, useTranslatedService } from '@/hooks/useTranslatedContent';
import {
  Heart,
  Star,
  MapPin,
  Phone,
  ExternalLink,
  Navigation,
  ChevronDown,
  ChevronUp,
  Package,
  ShoppingBag,
  Bike,
  Wrench,
  Gift,
  ConciergeBell,
  Compass,
  HeartHandshake,
  FileText,
  Download,
  ZoomIn,
  Quote,
  MessageCircle,
} from 'lucide-react';
import { MapEmbed } from '@/components/ui/MapEmbed';
import { formatCurrency } from '@/lib/currency';
import type { LocalRecommendation, LandlordService, GuestHostUpdate } from '@/types/database';
import { getCategoryInfo } from '@/hooks/useLocalRecommendations';
import { RecommendationIcon } from './recommendationIcons';
import { HostUpdatesSection } from './HostUpdatesSection';

interface HostCornerTabProps {
  personalRecommendations: LocalRecommendation[];
  services: LandlordService[];
  hostName: string;
  hostAvatar?: string | null;
  darkMode: boolean;
  updates?: GuestHostUpdate[];
}

// Personal Recommendation Card - Special design for host favorites
function PersonalRecommendationCard({
  recommendation: rawRecommendation,
  darkMode,
}: {
  recommendation: LocalRecommendation;
  darkMode: boolean;
}) {
  const { t } = useTranslation();
  // Host-written texts in the guest's language (name stays as written).
  const { translations: tr } = useTranslatedRecommendation(rawRecommendation);
  const recommendation: LocalRecommendation = {
    ...rawRecommendation,
    description: tr.description?.translated || rawRecommendation.description,
    how_to_get_there: tr.how_to_get_there?.translated || rawRecommendation.how_to_get_there,
    insider_tip: tr.insider_tip?.translated || rawRecommendation.insider_tip,
    personal_note: tr.personal_note?.translated || rawRecommendation.personal_note,
  };
  const [showMap, setShowMap] = useState(false);
  const categoryInfo = getCategoryInfo(recommendation.category);
  const hasCoordinates = recommendation.latitude && recommendation.longitude;

  const handleNavigate = () => {
    if (recommendation.google_maps_url) {
      window.open(recommendation.google_maps_url, '_blank');
    } else if (hasCoordinates) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${recommendation.latitude},${recommendation.longitude}`,
        '_blank'
      );
    }
  };

  return (
    <div
      className={`rounded-2xl overflow-hidden ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border border-cyan-100 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.15)]'
      }`}
    >
      <div className="p-4">
        {/* Header with heart */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl shrink-0 relative">
            {recommendation.image_url ? (
              <img
                src={recommendation.image_url}
                alt={recommendation.name}
                className="w-10 h-10 rounded-xl object-cover"
              />
            ) : (
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  darkMode ? 'bg-cyan-500/20' : 'bg-cyan-50'
                }`}
              >
                <RecommendationIcon category={recommendation.category} className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              </div>
            )}
            <Heart className="absolute -top-1 -right-1 w-5 h-5 text-rose-500 fill-rose-500 drop-shadow-md" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-bold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {recommendation.name}
            </h4>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {t(`localGuide.categories.${recommendation.category}`, categoryInfo.label)}
              {recommendation.distance_km && ` • ${recommendation.distance_km} km`}
            </p>
          </div>
        </div>

        {/* Rating */}
        {recommendation.landlord_rating && (
          <div className="flex items-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-4 h-4 ${
                  star <= recommendation.landlord_rating!
                    ? 'text-yellow-500 fill-yellow-500'
                    : darkMode
                    ? 'text-slate-600'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* Personal Note - The heart of host's recommendation */}
        {recommendation.personal_note && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <div className="flex items-start gap-2">
              <Quote className={`w-4 h-4 shrink-0 mt-0.5 ${darkMode ? 'text-teal-400' : 'text-teal-500'}`} />
              <p
                className={`text-sm italic leading-relaxed ${
                  darkMode ? 'text-slate-200' : 'text-gray-700'
                }`}
              >
                "{recommendation.personal_note}"
              </p>
            </div>
          </div>
        )}

        {/* Description if no personal note */}
        {!recommendation.personal_note && recommendation.description && (
          <p className={`text-sm mt-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            {recommendation.description}
          </p>
        )}

        {/* Address */}
        {recommendation.address && (
          <button
            onClick={() => hasCoordinates && setShowMap(!showMap)}
            className={`w-full mt-3 flex items-center justify-between gap-2 text-left ${
              hasCoordinates ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            <p
              className={`text-sm flex items-center gap-2 ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="truncate">{recommendation.address}</span>
            </p>
            {hasCoordinates && (
              <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>
                {showMap ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            )}
          </button>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {(recommendation.google_maps_url || hasCoordinates) && (
            <button
              onClick={handleNavigate}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Navigation className="w-4 h-4" />
              {t('guest.explore.navigate')}
            </button>
          )}
          {recommendation.phone && (
            <button
              onClick={() => (window.location.href = `tel:${recommendation.phone}`)}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-green-50 text-green-600 hover:bg-green-100'
              }`}
            >
              <Phone className="w-4 h-4" />
            </button>
          )}
          {recommendation.website && (
            <button
              onClick={() => window.open(recommendation.website!, '_blank')}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expandable Map */}
      {showMap && hasCoordinates && (
        <div className={`border-t ${darkMode ? 'border-pink-500/20' : 'border-pink-100'}`}>
          <MapEmbed
            latitude={recommendation.latitude!}
            longitude={recommendation.longitude!}
            title={recommendation.name}
            zoom={15}
            height="160px"
            darkMode={darkMode}
            showOpenButton={true}
            className="rounded-none"
          />
        </div>
      )}
    </div>
  );
}

// Service Card for products and services
function ServiceCard({
  service,
  darkMode,
}: {
  service: LandlordService;
  darkMode: boolean;
}) {
  const { t } = useTranslation();
  // Host writes in their own language; show the guest's.
  const { translations } = useTranslatedService(service);
  const name = translations.name?.translated || service.name;
  const description = translations.description?.translated || service.description;
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  useEffect(() => {
    if (showImageLightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showImageLightbox]);

  const formatPrice = () => {
    if (service.price_type === 'free') return t('guest.explore.free');
    if (!service.price) return '';

    const priceStr = formatCurrency(service.price, service.currency);
    switch (service.price_type) {
      case 'per_day':
        return `${priceStr}/${t('common.day')}`;
      case 'per_hour':
        return `${priceStr}/${t('common.hour')}`;
      case 'per_person':
        return `${priceStr}/${t('common.person')}`;
      default:
        return priceStr;
    }
  };

  const getServiceIcon = () => {
    switch (service.service_type) {
      case 'product':
        return <ShoppingBag className="w-4 h-4" />;
      case 'rental':
        return <Bike className="w-4 h-4" />;
      case 'service':
        return <Wrench className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getServiceColor = () => {
    if (darkMode) {
      // Solid dark surface for all service types (matches Accommodation tab).
      const border =
        service.service_type === 'product'
          ? 'border-amber-500/30'
          : service.service_type === 'rental'
          ? 'border-blue-500/30'
          : service.service_type === 'service'
          ? 'border-cyan-500/30'
          : 'border-slate-700';
      return `bg-slate-800 ${border} shadow-xl shadow-black/40 ring-1 ring-white/5`;
    }
    // Flat white surface in light mode, keeping a subtle type-specific border accent.
    switch (service.service_type) {
      case 'product':
        return 'bg-white border-amber-200/60';
      case 'rental':
        return 'bg-white border-blue-200/60';
      case 'service':
        return 'bg-white border-cyan-200/60';
      default:
        return 'bg-white border-gray-200/60';
    }
  };

  const getIconBgColor = () => {
    switch (service.service_type) {
      case 'product':
        return darkMode ? 'bg-amber-500/30 text-amber-300' : 'bg-amber-100 text-amber-700';
      case 'rental':
        return darkMode ? 'bg-blue-500/30 text-blue-300' : 'bg-blue-100 text-blue-700';
      case 'service':
        return darkMode ? 'bg-cyan-500/30 text-cyan-300' : 'bg-cyan-100 text-cyan-700';
      default:
        return darkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-200 text-gray-600';
    }
  };

  return (
    <>
      <div
        className={`rounded-2xl overflow-hidden border ${getServiceColor()} ${
          !darkMode ? 'shadow-[0_4px_20px_-2px_rgba(0,0,0,0.15)]' : ''
        } ${!service.is_available ? 'opacity-60' : ''}`}
      >
        {/* Service Image */}
        {service.image_url && (
          <button
            onClick={() => setShowImageLightbox(true)}
            className="relative w-full h-40 overflow-hidden group"
          >
            <img
              src={service.image_url}
              alt={name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
        )}

        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${getIconBgColor()}`}>
              {getServiceIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {name}
                </h4>
                {!service.is_available && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {t('guest.explore.unavailable')}
                  </span>
                )}
              </div>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {t(`localGuide.serviceTypes.${service.service_type}`)}
              </p>
              <p
                className={`text-lg font-bold mt-1 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}
              >
                {formatPrice()}
              </p>
            </div>
          </div>

          {description && (
            <p className={`text-sm mt-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
              {description}
            </p>
          )}

          {/* Document Link */}
          {service.document_url && (
            <a
              href={service.document_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 mt-4 p-3 rounded-xl transition-colors ${
                darkMode
                  ? 'bg-slate-700/50 hover:bg-slate-600/50'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <FileText className={`w-8 h-8 shrink-0 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {service.document_name || t('guest.hostCorner.viewDocument', 'Pogledaj dokument')}
                </p>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  PDF
                </p>
              </div>
              <Download className={`w-5 h-5 shrink-0 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            </a>
          )}

          {service.is_available && !service.document_url && (
            <p className={`flex items-center gap-1.5 text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              <MessageCircle className="w-3.5 h-3.5 shrink-0" />
              {t('guest.hostCorner.askHost', 'Pitajte domaćina za više informacija')}
            </p>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {showImageLightbox && service.image_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImageLightbox(false)}
        >
          <button
            onClick={() => setShowImageLightbox(false)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={service.image_url}
            alt={name}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// Main Component
export function HostCornerTab({
  personalRecommendations,
  services,
  hostName,
  hostAvatar,
  darkMode,
  updates = [],
}: HostCornerTabProps) {
  const { t } = useTranslation();

  const hasContent = personalRecommendations.length > 0 || services.length > 0 || updates.length > 0;

  // Separate services by type
  const products = services.filter((s) => s.service_type === 'product');
  const rentals = services.filter((s) => s.service_type === 'rental');
  const serviceItems = services.filter((s) => s.service_type === 'service');

  if (!hasContent) {
    return (
      <div
        className={`text-center py-12 rounded-2xl ${
          darkMode ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5' : 'bg-white shadow-lg'
        }`}
      >
        <Gift className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('guest.hostCorner.empty', 'Domaćin još nije dodao preporuke')}
        </h3>
        <p className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
          {t('guest.hostCorner.emptyDescription', 'Provjerite ponovno kasnije ili pitajte domaćina za savjete!')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={`rounded-2xl p-5 ${
          darkMode
            ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
            : 'bg-white border border-cyan-100 shadow-lg shadow-gray-200/80'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            {hostAvatar ? (
              <img
                src={hostAvatar}
                alt={hostName}
                className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20"
              />
            ) : (
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'
                }`}
              >
                <HeartHandshake className={`w-6 h-6 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              </div>
            )}
          </div>
          <div>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.hostCorner.title', 'Od domaćina')}
            </h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('guest.hostCorner.subtitle', 'Osobne preporuke i ponuda od {{name}}', { name: hostName })}
            </p>
          </div>
        </div>
      </div>

      {/* Host Updates (photos & announcements) */}
      {updates.length > 0 && (
        <HostUpdatesSection updates={updates} darkMode={darkMode} />
      )}

      {/* ═══ SECTION 1: Ponuda domaćina (Products, Rentals, Services) ═══ */}
      {services.length > 0 && (
        <div className="space-y-4">
          {/* Section header — group icon "outside" (gradient badge) + title */}
          <div className="flex items-center gap-3 pt-1">
            <div className="grid place-items-center h-11 w-11 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-600 shadow-lg shadow-cyan-500/30 shrink-0">
              <ConciergeBell className="w-6 h-6 text-white drop-shadow-sm" />
            </div>
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.hostCorner.offerSection', 'Ponuda domaćina')}
            </h3>
            <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
          </div>

          {/* Products */}
          {products.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className={`w-5 h-5 ${darkMode ? 'text-teal-400' : 'text-teal-600'}`} />
                <h4 className={`font-bold text-base tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('guest.hostCorner.products', 'Domaći proizvodi')}
                </h4>
              </div>
              <div className="grid gap-3">
                {products.map((service) => (
                  <ServiceCard key={service.id} service={service} darkMode={darkMode} />
                ))}
              </div>
            </div>
          )}

          {/* Rentals */}
          {rentals.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Bike className={`w-5 h-5 ${darkMode ? 'text-sky-400' : 'text-sky-600'}`} />
                <h4 className={`font-bold text-base tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('guest.hostCorner.rentals', 'Najam')}
                </h4>
              </div>
              <div className="grid gap-3">
                {rentals.map((service) => (
                  <ServiceCard key={service.id} service={service} darkMode={darkMode} />
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          {serviceItems.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Wrench className={`w-5 h-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                <h4 className={`font-bold text-base tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('guest.hostCorner.services', 'Usluge')}
                </h4>
              </div>
              <div className="grid gap-3">
                {serviceItems.map((service) => (
                  <ServiceCard key={service.id} service={service} darkMode={darkMode} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ SECTION 2: Preporuke domaćina (Personal Recommendations) ═══ */}
      {personalRecommendations.length > 0 && (
        <div className="space-y-4">
          {/* Section header — group icon "outside" (gradient badge) + title */}
          <div className="flex items-center gap-3 pt-1">
            <div className="grid place-items-center h-11 w-11 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-600 shadow-lg shadow-sky-500/30 shrink-0">
              <Compass className="w-6 h-6 text-white drop-shadow-sm" />
            </div>
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.hostCorner.recommendationsSection', 'Preporuke domaćina')}
            </h3>
            <span className={`text-sm ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              ({personalRecommendations.length})
            </span>
            <div className={`flex-1 h-px ${darkMode ? 'bg-slate-700/50' : 'bg-gray-200'}`} />
          </div>

          <div className="space-y-4">
            {personalRecommendations.map((rec) => (
              <PersonalRecommendationCard key={rec.id} recommendation={rec} darkMode={darkMode} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default HostCornerTab;
